import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Popover,
  Stack,
  Table,
  Text,
  Tooltip,
  type TableProps,
} from '@mantine/core';
import {
  IconArrowAutofitWidth,
  IconChevronLeft,
  IconChevronRight,
  IconColumns3,
} from '@tabler/icons-react';
import { useTranslation } from '../i18n/LocaleContext';
import classes from './DataTable.module.css';

/**
 * Column definition for {@link DataTable}. Widths are pixels; the table uses
 * `table-layout: fixed`, so a column never grows past its width — cells clip
 * with an ellipsis instead.
 */
export type DataTableColumn = {
  /** Stable id — also the localStorage key for the persisted width/visibility. */
  key: string;
  label?: ReactNode;
  /** Default width in px. */
  width: number;
  /** Lower bound while resizing (default 60px, 36px for `fixed` columns). */
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  /**
   * Pin the column while scrolling horizontally. Left-pinned columns must be
   * the first ones in `columns`, right-pinned the last ones.
   */
  sticky?: 'left' | 'right';
  /** No resize handle and not hideable (icon / action columns). */
  fixed?: boolean;
};

type DataTableProps = {
  /** Unique per table — namespaces the persisted widths + hidden columns. */
  tableId: string;
  columns: readonly DataTableColumn[];
  /** `<Table.Tr>` rows (rendered inside `<Table.Tbody>`). */
  children: ReactNode;
  /** Rendered at the left of the toolbar above the grid (e.g. pagination). */
  toolbar?: ReactNode;
} & Pick<TableProps, 'striped' | 'highlightOnHover' | 'verticalSpacing' | 'horizontalSpacing'>;

const STORAGE_PREFIX = 'transpay.table.';
const KEY_STEP = 16;

function loadJson<T>(key: string, guard: (v: unknown) => T | null): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return guard(JSON.parse(raw));
  } catch {
    return null;
  }
}
function saveJson(key: string, value: unknown, empty: boolean): void {
  try {
    if (empty) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — preferences just won't persist */
  }
}
function loadWidths(tableId: string): Record<string, number> {
  return (
    loadJson(`${STORAGE_PREFIX}${tableId}.widths`, (v) => {
      if (!v || typeof v !== 'object') return null;
      const out: Record<string, number> = {};
      for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
        if (typeof n === 'number' && Number.isFinite(n) && n > 0) out[k] = n;
      }
      return out;
    }) ?? {}
  );
}
function loadHidden(tableId: string): string[] {
  return (
    loadJson(`${STORAGE_PREFIX}${tableId}.hidden`, (v) =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null,
    ) ?? []
  );
}
function minWidthOf(c: DataTableColumn): number {
  return c.minWidth ?? (c.fixed ? 36 : 60);
}

/**
 * Wide data grid used by the list pages. Horizontal navigation strategy:
 *
 *  1. **One horizontal scrollbar that is always reachable** — the grid's own
 *     scrollbar is hidden and replaced by a proxy bar that is `position:
 *     sticky; bottom: 0`, so it stays glued to the bottom of the window while
 *     any part of the table is on screen (no more scrolling to the end of 25
 *     rows to find it). Vertical scrolling stays with the window.
 *  2. **Pinned columns** (`sticky`) keep the row identity and the money/action
 *     columns visible however far the user scrolls sideways.
 *  3. **Column chooser** — hide the columns you don't need (persisted per
 *     table); with fewer columns the grid usually needs no scrolling at all.
 *  4. **Page-wise scroll buttons** (◀ ▶) in the toolbar, plus the native
 *     shift + mouse-wheel / trackpad swipe on the grid.
 *  5. **Resizable columns** — drag the handle at a header's right edge
 *     (keyboard: focus it, ← / → resize, Home resets); widths persist per
 *     table and a reset button appears once they differ from defaults.
 *
 * Row markup stays plain `<Table.Tr>` / `<Table.Td>` in the page — only the
 * header is generated here, so a `<Table.Td>` per column (in order) is all a
 * page needs. Pinned / hidden columns are applied to body cells by position.
 */
export function DataTable({
  tableId,
  columns,
  children,
  toolbar,
  striped,
  highlightOnHover = true,
  verticalSpacing,
  horizontalSpacing,
}: DataTableProps) {
  const { t } = useTranslation();
  const [custom, setCustom] = useState<Record<string, number>>(() => loadWidths(tableId));
  const [hidden, setHidden] = useState<string[]>(() => loadHidden(tableId));
  const [resizing, setResizing] = useState<string | null>(null);
  const drag = useRef<{ key: string; startX: number; startWidth: number } | null>(null);

  // Re-read when the table identity changes (same component, other tab).
  useEffect(() => {
    setCustom(loadWidths(tableId));
    setHidden(loadHidden(tableId));
  }, [tableId]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const isHidden = (c: DataTableColumn) => !c.fixed && hiddenSet.has(c.key);

  const widths = useMemo(
    () =>
      columns.map((c) =>
        !c.fixed && hiddenSet.has(c.key) ? 0 : Math.max(minWidthOf(c), custom[c.key] ?? c.width),
      ),
    [columns, custom, hiddenSet],
  );
  const totalWidth = widths.reduce((s, w) => s + w, 0);
  const customised = columns.some((c) => custom[c.key] != null && custom[c.key] !== c.width);

  // Sticky offsets: left-pinned columns accumulate from the left edge,
  // right-pinned from the right edge (hidden columns contribute 0).
  const stickyStyles = useMemo(() => {
    const out: Array<CSSProperties | undefined> = columns.map(() => undefined);
    let left = 0;
    for (let i = 0; i < columns.length; i += 1) {
      if (columns[i]!.sticky !== 'left') break;
      out[i] = { left };
      left += widths[i]!;
    }
    let right = 0;
    for (let i = columns.length - 1; i >= 0; i -= 1) {
      if (columns[i]!.sticky !== 'right') break;
      out[i] = { right };
      right += widths[i]!;
    }
    return out;
  }, [columns, widths]);
  // Innermost VISIBLE pinned column on each side gets the edge shadow.
  let lastLeftIdx = -1;
  for (let i = 0; i < columns.length && columns[i]!.sticky === 'left'; i += 1) {
    if (widths[i]! > 0) lastLeftIdx = i;
  }
  let firstRightIdx = -1;
  for (let i = columns.length - 1; i >= 0 && columns[i]!.sticky === 'right'; i -= 1) {
    if (widths[i]! > 0) firstRightIdx = i;
  }

  // ----- width persistence -----
  const commitWidths = useCallback(
    (next: Record<string, number>) => {
      setCustom(next);
      saveJson(`${STORAGE_PREFIX}${tableId}.widths`, next, Object.keys(next).length === 0);
    },
    [tableId],
  );
  const setWidth = useCallback(
    (key: string, px: number, persist: boolean) => {
      const col = columns.find((c) => c.key === key);
      if (!col) return;
      const clamped = Math.round(Math.max(minWidthOf(col), px));
      setCustom((prev) => {
        const next = { ...prev, [key]: clamped };
        if (persist) saveJson(`${STORAGE_PREFIX}${tableId}.widths`, next, false);
        return next;
      });
    },
    [columns, tableId],
  );
  const commitHidden = (next: string[]) => {
    setHidden(next);
    saveJson(`${STORAGE_PREFIX}${tableId}.hidden`, next, next.length === 0);
  };

  // ----- resize handle -----
  const onPointerDown = (key: string, idx: number) => (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { key, startX: e.clientX, startWidth: widths[idx]! };
    setResizing(key);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setWidth(d.key, d.startWidth + (e.clientX - d.startX), false);
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    setResizing(null);
    setWidth(d.key, d.startWidth + (e.clientX - d.startX), true);
  };
  const resetOne = (key: string) => {
    const next = { ...custom };
    delete next[key];
    commitWidths(next);
  };
  const onKeyDown = (key: string, idx: number) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setWidth(key, widths[idx]! + (e.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP), true);
    } else if (e.key === 'Home') {
      e.preventDefault();
      resetOne(key);
    }
  };

  // ----- horizontal scrolling: hidden native bar + sticky proxy bar -----
  const viewportRef = useRef<HTMLDivElement>(null);
  const proxyRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [scrollW, setScrollW] = useState(0);
  const [clientW, setClientW] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const measure = () => {
      setScrollW(vp.scrollWidth);
      setClientW(vp.clientWidth);
      setScrollLeft(vp.scrollLeft);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    if (vp.firstElementChild) ro.observe(vp.firstElementChild);
    return () => ro.disconnect();
  }, [totalWidth, children]);

  const onViewportScroll = () => {
    const vp = viewportRef.current;
    const px = proxyRef.current;
    if (!vp) return;
    setScrollLeft(vp.scrollLeft);
    if (px && !syncing.current) {
      syncing.current = true;
      px.scrollLeft = vp.scrollLeft;
      syncing.current = false;
    }
  };
  const onProxyScroll = () => {
    const vp = viewportRef.current;
    const px = proxyRef.current;
    if (!vp || !px || syncing.current) return;
    syncing.current = true;
    vp.scrollLeft = px.scrollLeft;
    syncing.current = false;
  };
  const scrollByPage = (dir: -1 | 1) => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.scrollBy({ left: dir * Math.max(200, vp.clientWidth * 0.6), behavior: 'smooth' });
  };
  const overflowing = scrollW > clientW + 1;
  const atStart = scrollLeft <= 1;
  const atEnd = scrollLeft + clientW >= scrollW - 1;

  // ----- pinned / hidden body cells (rows are the page's own <Table.Td>s) -----
  const scopeClass = `dt-${tableId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const bodyCss = useMemo(() => {
    const rules: string[] = [];
    columns.forEach((c, i) => {
      const sel = `.${scopeClass} tbody td:nth-child(${i + 1}):not([colspan])`;
      if (!c.fixed && hiddenSet.has(c.key)) {
        // NOT display:none — that would shift the cell→column mapping of the
        // fixed layout. A zero-width, clipped cell keeps the indexes intact.
        rules.push(`${sel}{width:0;max-width:0;padding-left:0!important;padding-right:0!important;overflow:hidden;border-width:0}`);
        return;
      }
      const st = stickyStyles[i];
      if (!c.sticky || !st) return;
      const offset = c.sticky === 'left' ? `left:${st.left}px` : `right:${st.right}px`;
      rules.push(
        `${sel}{position:sticky;${offset};z-index:2;background:var(--mantine-color-body)}`,
        `.${scopeClass} tbody tr:hover td:nth-child(${i + 1}):not([colspan]){background:var(--table-hover-color,var(--mantine-color-default-hover))}`,
      );
      if (i === lastLeftIdx || i === firstRightIdx) {
        rules.push(
          `${sel}{box-shadow:inset ${c.sticky === 'left' ? '-1px' : '1px'} 0 0 var(--mantine-color-default-border)}`,
        );
      }
    });
    return rules.join('\n');
  }, [columns, stickyStyles, scopeClass, lastLeftIdx, firstRightIdx, hiddenSet]);

  const hideable = columns.filter((c) => !c.fixed && c.label != null);

  return (
    <div className={`${classes.wrap} ${scopeClass}`} data-resizing={resizing ? 'true' : 'false'}>
      {bodyCss && <style>{bodyCss}</style>}

      <Group justify="space-between" px="md" py="xs" className={classes.toolbar} wrap="wrap">
        <div>{toolbar}</div>
        <Group gap={6} wrap="nowrap">
          {overflowing && (
            <>
              <Tooltip label={t('table.scrollLeft')} withArrow>
                <ActionIcon
                  variant="default"
                  size="sm"
                  aria-label={t('table.scrollLeft')}
                  disabled={atStart}
                  onClick={() => scrollByPage(-1)}
                >
                  <IconChevronLeft size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t('table.scrollRight')} withArrow>
                <ActionIcon
                  variant="default"
                  size="sm"
                  aria-label={t('table.scrollRight')}
                  disabled={atEnd}
                  onClick={() => scrollByPage(1)}
                >
                  <IconChevronRight size={14} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          {customised && (
            <Tooltip label={t('table.resetWidths')} withArrow>
              <ActionIcon
                variant="default"
                size="sm"
                aria-label={t('table.resetWidths')}
                onClick={() => commitWidths({})}
              >
                <IconArrowAutofitWidth size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {hideable.length > 0 && (
            <Popover position="bottom-end" shadow="md" withArrow>
              <Popover.Target>
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconColumns3 size={14} />}
                  rightSection={
                    hidden.length > 0 ? (
                      <Text size="xs" c="dimmed">
                        −{hidden.length}
                      </Text>
                    ) : undefined
                  }
                >
                  {t('table.columns')}
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap={6}>
                  <Group justify="space-between" gap="md">
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                      {t('table.columns')}
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      disabled={hidden.length === 0}
                      onClick={() => commitHidden([])}
                    >
                      {t('table.showAll')}
                    </Button>
                  </Group>
                  {hideable.map((c) => (
                    <Checkbox
                      key={c.key}
                      size="xs"
                      label={c.label}
                      checked={!hiddenSet.has(c.key)}
                      onChange={(e) =>
                        commitHidden(
                          e.currentTarget.checked
                            ? hidden.filter((k) => k !== c.key)
                            : [...hidden, c.key],
                        )
                      }
                    />
                  ))}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
      </Group>

      <div ref={viewportRef} className={classes.viewport} onScroll={onViewportScroll}>
        <Table
          className={classes.table}
          layout="fixed"
          w="100%"
          miw={totalWidth}
          striped={striped}
          highlightOnHover={highlightOnHover}
          verticalSpacing={verticalSpacing}
          horizontalSpacing={horizontalSpacing}
        >
          <colgroup>
            {columns.map((c, i) => (
              <col key={c.key} style={{ width: widths[i] }} />
            ))}
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c, i) => (
                <Table.Th
                  key={c.key}
                  className={[
                    classes.th,
                    c.sticky === 'left' ? classes.stickyLeft : '',
                    c.sticky === 'right' ? classes.stickyRight : '',
                    isHidden(c) ? classes.hiddenCol : '',
                  ].join(' ')}
                  data-align={c.align ?? 'left'}
                  data-edge={i === lastLeftIdx || i === firstRightIdx ? 'true' : 'false'}
                  style={{ width: widths[i], ...stickyStyles[i] }}
                  title={typeof c.label === 'string' ? c.label : undefined}
                >
                  <span className={classes.thLabel}>{c.label}</span>
                  {!c.fixed && (
                    <div
                      className={classes.resizer}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t('table.resizeColumn')}
                      aria-valuenow={widths[i]}
                      aria-valuemin={minWidthOf(c)}
                      tabIndex={0}
                      data-active={resizing === c.key ? 'true' : 'false'}
                      onPointerDown={onPointerDown(c.key, i)}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                      onDoubleClick={() => resetOne(c.key)}
                      onKeyDown={onKeyDown(c.key, i)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{children}</Table.Tbody>
        </Table>
      </div>

      {/* Sticky proxy scrollbar: mirrors the grid's horizontal scroll and stays
          at the bottom of the window while the table is in view. */}
      <div
        ref={proxyRef}
        className={classes.proxy}
        onScroll={onProxyScroll}
        data-visible={overflowing ? 'true' : 'false'}
        aria-hidden="true"
      >
        <div style={{ width: scrollW }} />
      </div>
    </div>
  );
}
